import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PanelSkeleton, TableSkeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ItemSelectionPanel } from '../components/ItemSelectionPanel';
import { picklistService } from '../services';
import { usePicklistStore } from '../store';
import type { CreatePicklistItemRequest } from '../types';
import { addItemsCounts } from '../utils/picklistProgress';

export function PicklistAddItemsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    selectedPicklist,
    isLoadingDetail,
    detailError,
    fetchPicklistById,
    clearSelectedPicklist,
  } = usePicklistStore();

  usePageTitle(selectedPicklist ? `Add Items to ${selectedPicklist.picklistNumber}` : 'Add Items to Picklist');

  useEffect(() => {
    if (id) {
      fetchPicklistById(Number(id));
    }
    return () => {
      clearSelectedPicklist();
    };
  }, [id, fetchPicklistById, clearSelectedPicklist]);

  /**
   * The lines already on this picklist. NOT an exclusion list any more (owner ruling 2026-09-22): a line
   * with quantity still unallocated is shown and topped up, so the panel needs the quantities and the
   * bins, not just the ids. See `ItemSelectionPanel`'s `existingPicklistItems`.
   */
  const existingItems = useMemo(() => selectedPicklist?.items ?? [], [selectedPicklist]);

  const handleAddItems = async (items: CreatePicklistItemRequest[]) => {
    const anchorsBefore = new Set((selectedPicklist?.itemGroups ?? []).map((g) => g.id));
    const updated = await picklistService.addItems(Number(id), { items });

    // A request can do two things at once — put new items on the picklist and increase items it already had
    // — so the confirmation counts them separately. It counts ITEM ROWS, as the Activity row does (PT-7 F8):
    // a new MRS line of an item already here grows that item's row, so it is "increased", not "added".
    const { added, increased } = addItemsCounts(
      anchorsBefore,
      updated.itemGroups ?? [],
      items.map((i) => i.materialRequestItemId),
    );
    const plural = (n: number) => (n === 1 ? '' : 's');
    const target = selectedPicklist?.picklistNumber ?? 'picklist';

    toast.success(increased > 0 && added === 0 ? 'Quantities Increased' : 'Items Added', {
      description:
        increased === 0
          ? `Added ${added} item${plural(added)} to ${target}`
          : added === 0
            ? `Increased ${increased} item${plural(increased)} on ${target}`
            : `Added ${added} item${plural(added)} and increased ${increased} on ${target}`,
    });

    // Navigate back to the picklist detail page
    navigate(`/mro/issuance/picklist/${id}`);
  };

  // Show loading while fetching picklist details — shaped like the panel + table
  // that ItemSelectionPanel renders, so the swap-in is visually stable.
  if (isLoadingDetail) {
    return (
      <div className="space-y-4">
        <PanelSkeleton rows={2} />
        <TableSkeleton rows={8} cols={6} />
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-destructive">{detailError}</p>
        <button
          onClick={() => id && fetchPicklistById(Number(id))}
          className="text-sm text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const picklistNumber = selectedPicklist?.picklistNumber ?? '...';

  return (
    <ItemSelectionPanel
      title="Add items"
      // The leading crumb is the LIST; the picklist gets its own crumb in between, so the picklist
      // you are adding to stays one click away instead of being spelled out in the title.
      backPath="/mro/issuance"
      picklistNumber={picklistNumber}
      picklistPath={`/mro/issuance/picklist/${id}`}
      submitLabel="Add to Picklist"
      submittingLabel="Adding..."
      confirmTitle="Confirm Add Items"
      confirmDescription="Review the items before adding to the picklist."
      confirmButtonLabel="Add Items"
      existingPicklistItems={existingItems}
      targetPicklistId={Number(id)}
      onSubmit={handleAddItems}
    />
  );
}
